import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = 'https://puffies-b24ce.ue.r.appspot.com/api';

export default function EditStuffie() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, isAdmin } = useAuth();
  
  const [stuffie, setStuffie] = useState({
    name: '',
    description: '',
    status: 'Not Purchased',
    birthday: '',
    wanted: false
  });
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchStuffie = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/stuffed-animals/${id}`);
      setStuffie(response.data);
    } catch (error) {
      setError('Error fetching stuffed animal: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!currentUser || !isAdmin) {
      navigate('/');
      return;
    }
    
    if (id) {
      fetchStuffie();
    } else {
      setLoading(false);
    }
  }, [id, currentUser, isAdmin, navigate, fetchStuffie]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      setLoading(true);
      const token = await currentUser.getIdToken();
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };
      
      if (id) {
        if (!image) {
          const data = {
            name: stuffie.name,
            description: stuffie.description,
            status: stuffie.status,
            birthday: stuffie.birthday,
            wanted: stuffie.wanted
          };
          await axios.patch(`${API_BASE_URL}/stuffed-animals/${id}`, data, config);
        } else {
          const formData = new FormData();
          formData.append('name', stuffie.name);
          formData.append('description', stuffie.description);
          formData.append('status', stuffie.status);
          formData.append('birthday', stuffie.birthday);
          formData.append('wanted', stuffie.wanted);
          formData.append('image', image);
          
          config.headers['Content-Type'] = 'multipart/form-data';
          await axios.patch(`${API_BASE_URL}/stuffed-animals/${id}`, formData, config);
        }
        setSuccess('Stuffed animal updated successfully!');
      } else {
        const formData = new FormData();
        formData.append('name', stuffie.name);
        formData.append('description', stuffie.description);
        formData.append('status', stuffie.status);
        formData.append('birthday', stuffie.birthday);
        formData.append('wanted', stuffie.wanted);
        formData.append('image', image);
        
        config.headers['Content-Type'] = 'multipart/form-data';
        await axios.post(`${API_BASE_URL}/stuffed-animals`, formData, config);
        setSuccess('New stuffed animal added successfully!');
      }
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      setError('Error saving stuffed animal: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this stuffed animal?')) {
      return;
    }

    try {
      setLoading(true);
      const token = await currentUser.getIdToken();
      await axios.delete(`${API_BASE_URL}/stuffed-animals/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSuccess('Stuffed animal deleted successfully!');
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      setError('Error deleting stuffed animal: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser || !isAdmin) {
    return null;
  }

  return (
    <Container component="main" maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Typography component="h1" variant="h5" gutterBottom>
          {id ? 'Edit Stuffed Animal' : 'Add New Stuffed Animal'}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Name"
            value={stuffie.name}
            onChange={(e) => setStuffie({ ...stuffie, name: e.target.value })}
            disabled={loading}
          />
          
          <TextField
            margin="normal"
            fullWidth
            multiline
            rows={4}
            label="Description"
            value={stuffie.description}
            onChange={(e) => setStuffie({ ...stuffie, description: e.target.value })}
            disabled={loading}
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={stuffie.status}
              onChange={(e) => setStuffie({ ...stuffie, status: e.target.value })}
              label="Status"
              disabled={loading}
            >
              <MenuItem value="Not Purchased">Not Purchased</MenuItem>
              <MenuItem value="Purchased">Purchased</MenuItem>
              <MenuItem value="Lucy">Lucy</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            margin="normal"
            fullWidth
            label="Birthday (Month Day)"
            value={stuffie.birthday}
            onChange={(e) => setStuffie({ ...stuffie, birthday: e.target.value })}
            disabled={loading}
            helperText="Format: January 1"
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Wanted</InputLabel>
            <Select
              value={stuffie.wanted}
              onChange={(e) => setStuffie({ ...stuffie, wanted: e.target.value })}
              label="Wanted"
              disabled={loading}
            >
              <MenuItem value={true}>Yes</MenuItem>
              <MenuItem value={false}>No</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            margin="normal"
            fullWidth
            type="file"
            onChange={(e) => setImage(e.target.files[0])}
            disabled={loading}
            InputLabelProps={{ shrink: true }}
          />
          
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ flex: 1 }}
            >
              {loading ? <CircularProgress size={24} /> : (id ? 'Update' : 'Add')}
            </Button>
            
            {id && (
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={loading}
              >
                Delete
              </Button>
            )}
            
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
} 